import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';

import { Theme } from '@constants';
import {
  MapTileSizeMethod,
  useGetGriddedProductInventory,
  useGetGriddedProductTile,
  useLoadingManager,
  useMercatorMap,
} from '@hooks';

import { MapPositionedText } from './MapPositionedText';
import { OverlayType, OverlayTypeInfo } from './MapTypes';

import populatedPlaces from '../../assets/data/populatedPlaces.json';
import { TimeUtils } from '../../helpers/TimeUtils';
import { Units } from '../../helpers/Units';
// import { MapTileSizeMethod } from '../../hooks/useMercatorMap';

// Cache Hook

// We run into another instance where native is slower than web.  Here we are caching string sizes so we don't need to relayout the value strings every time the layer is drawn.
const useTextSizeCache = () => {
  const textSizeCache = useRef({});

  const cachedSize = text => {
    const cachedValue = textSizeCache.current[text];

    if (cachedValue != null && cachedValue.width != null && cachedValue.height != null) {
      return cachedValue;
    }

    return { width: 0, height: 0 };
  };

  const saveSize = (text, width, height) => {
    textSizeCache.current[text] = { width, height };
  };

  return {
    cachedSize,
    saveSize,
  };
};

// Data Parsing

// Dives into the inventory data and fetches just the dimension data we care about.
const dimensionsFromData = (inventoryData, overlayType) => {
  const productNumber = OverlayTypeInfo[overlayType].griddedProductNumber;
  const productVariable = OverlayTypeInfo[overlayType].griddedVariableID;

  if (productNumber == null || productVariable == null) {
    return null;
  }

  const layers = inventoryData?.data?.layers;

  if (layers != null) {
    const productData = layers[productNumber];

    if (productData != null) {
      const variableData = productData[productVariable];

      if (variableData != null) {
        return variableData.dimensions;
      }
    }
  }

  return null;
};

// Date Arithmetic

// Inspects the dimension data for the latest run time, then returns the data for that run time.
const dataWithLatestRT = dimensionData => {
  if (dimensionData == null || dimensionData.length === 0) {
    return null;
  }

  const sortedDimensionData = dimensionData.sort((a, b) => (a.rt > b.rt ? 1 : -1));

  return sortedDimensionData[sortedDimensionData.length - 1];
};

// Return the closest time to now from an array of epoch times.
const closestT = itemData => {
  if (itemData == null || itemData.t == null || itemData.t.length === 0) {
    return null;
  }

  const now = TimeUtils.nowInEpoch();

  let closestTime = itemData.t[0];

  for (const time of itemData.t) {
    if (Math.abs(time - now) < Math.abs(closestTime - now)) {
      closestTime = time;
    }
  }

  return closestTime;
};

// Value Arithmetic

// Handles the unit conversion from a raw value into a display value, based on overlay type.
const rawToDisplayValue = (rawValue, overlayType) => {
  if (rawValue === null) {
    return null;
  }

  switch (overlayType) {
    case OverlayType.temperature:
    case OverlayType.feelsLike:
      return Units.temperature.kToF(rawValue);

    case OverlayType.wind:
      return Units.speed.mpsToMPH(rawValue);
  }

  return rawValue;
};

// MapPlottedValuesLayerAPI

const MapPlottedValuesLayerAPI = forwardRef(({ alpha, center, zoom, overlayType, onOverlayReady }, reference) => {
  const { data: inventoryData } = useGetGriddedProductInventory(
    OverlayTypeInfo[overlayType].griddedProductNumber,
    OverlayTypeInfo[overlayType].griddedVariableID,
  );

  const dimensions = dimensionsFromData(inventoryData, overlayType);
  const latestData = dataWithLatestRT(dimensions);
  const closestTime = closestT(latestData);

  return (
    <MapPlottedValuesLayerUI
      alpha={alpha}
      center={center}
      overlayType={overlayType}
      ref={reference}
      rt={latestData?.rt}
      t={closestTime}
      zoom={zoom}
      onOverlayReady={onOverlayReady}
    />
  );
});

// MapPlottedValuesLayerUI

const MapPlottedValuesLayerUI = forwardRef(({ alpha, center, zoom, overlayType, onOverlayReady, t, rt }, reference) => {
  // This is the size of the rendered map component.  We use this while calculating map zooms.
  const [mapWidth, setMapWidth] = useState(null);
  const [mapHeight, setMapHeight] = useState(null);

  // We need two separate instances of useMercatorMap.
  // This one is for translating into the gridded data.
  const gridMercatorMap = useMercatorMap({
    center,
    zoom: Math.min(zoom, 5), // API only has data up through zoom 5.
    tileDimension: 256, // API requests return a 256x256 float grid.
    viewWidth: mapWidth ?? 1,
    viewHeight: mapHeight ?? 1,
    tileSizeMethod: MapTileSizeMethod.none,
  });

  // This second instance of useMercatorMap is for translating with the view.
  const viewMercatorMap = useMercatorMap({
    center,
    zoom,
    tileDimension: 512,
    viewWidth: (mapWidth ?? 1) * Theme.pixelRatio(),
    viewHeight: (mapHeight ?? 1) * Theme.pixelRatio(),
    tileSizeMethod: MapTileSizeMethod.none,
  });

  const textSizeCache = useTextSizeCache();

  const loadingManager = useLoadingManager(newIsLoading => {
    if (onOverlayReady !== undefined && !newIsLoading) {
      onOverlayReady();
    }
  });

  useEffect(() => {
    loadingManager.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center, zoom, t, rt, overlayType]);

  const tiles = [];

  if (mapWidth != null && mapHeight != null && rt != null && t != null) {
    const upperLeftCoords = viewMercatorMap.viewPointToCoords(0, 0);
    const bottomRightCoords = viewMercatorMap.viewPointToCoords(
      mapWidth * Theme.pixelRatio(),
      mapHeight * Theme.pixelRatio(),
    );

    const minRow = gridMercatorMap.tileRowForLatitude(upperLeftCoords[1]);
    const maxRow = gridMercatorMap.tileRowForLatitude(bottomRightCoords[1]);
    const minCol = gridMercatorMap.tileColumnForLongitude(upperLeftCoords[0]);
    const maxCol = gridMercatorMap.tileColumnForLongitude(bottomRightCoords[0]);

    for (let row = minRow; row <= maxRow; row++) {
      for (let col = minCol; col <= maxCol; col++) {
        const loadingKey = `MapPlottedValuesTile-${row}-${col}`;

        loadingManager.beginLoading(loadingKey);

        tiles.push(
          <MapPlottedValuesTile
            key={`${col}-${row}`}
            gridMercatorMap={gridMercatorMap}
            overlayType={overlayType}
            rt={rt}
            t={t}
            textSizeCache={textSizeCache}
            viewMercatorMap={viewMercatorMap}
            x={col}
            y={row}
            z={Math.floor(gridMercatorMap.tileZoom)}
            onLoad={() => loadingManager.finishedLoading(loadingKey)}
          />,
        );
      }
    }
  }

  // Create the text components here.
  return (
    <View
      ref={reference}
      style={[{ opacity: alpha }, styles.layerParent]}
      onLayout={event => {
        const { width, height } = event.nativeEvent.layout;

        if (width != null && width !== mapWidth) {
          setMapWidth(width);
        }
        if (height != null && height !== mapHeight) {
          setMapHeight(height);
        }
      }}
    >
      {tiles}
    </View>
  );
});

export const MapPlottedValuesLayer = {
  API: MapPlottedValuesLayerAPI,
  UI: MapPlottedValuesLayerUI,
};

MapPlottedValuesLayerAPI.propTypes = {
  alpha: PropTypes.number,
  center: PropTypes.arrayOf(PropTypes.number).isRequired,
  zoom: PropTypes.number.isRequired,
  overlayType: PropTypes.string.isRequired,
  onOverlayReady: PropTypes.func,
};

MapPlottedValuesLayerAPI.defaultProps = {
  alpha: 1,
  onOverlayReady: () => {},
};

// MapPlottedValuesTile

// Gathers gridded data for a tile and then plots based populated places that reside in that tile area.
const MapPlottedValuesTile = ({
  gridMercatorMap,
  viewMercatorMap,
  onLoad,
  overlayType,
  textSizeCache,
  t,
  rt,
  x,
  y,
  z,
}) => {
  const tileGeoRect = gridMercatorMap.geoRectForTile(x, y);

  const gridIndexFor = (longitude, latitude) => {
    const tileX = Math.round(gridMercatorMap.longitudeToTileX(longitude));
    const tileY = Math.round(gridMercatorMap.latitudeToTileY(latitude));

    const baseX = x * gridMercatorMap.tileDimension;
    const baseY = y * gridMercatorMap.tileDimension;

    return (tileY - baseY) * gridMercatorMap.tileDimension + (tileX - baseX);
  };

  const tilePopulatedPlaces = useMemo(
    () =>
      populatedPlaces.filter(
        place =>
          place.latitude >= tileGeoRect.minLatitude &&
          place.latitude < tileGeoRect.maxLatitude &&
          place.longitude >= tileGeoRect.minLongitude &&
          place.longitude < tileGeoRect.maxLongitude,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [x, y, z],
  );

  const { data: gridData } = useGetGriddedProductTile(
    OverlayTypeInfo[overlayType].griddedProductNumber,
    OverlayTypeInfo[overlayType].griddedVariableID,
    rt,
    t,
    x,
    y,
    z,
  );

  gridData && onLoad();

  return gridData == null
    ? null
    : tilePopulatedPlaces.map(place => {
        const gridIndex = gridIndexFor(place.longitude, place.latitude);
        const viewPosition = viewMercatorMap.coordsToViewPoint(place.longitude, place.latitude);

        if (gridIndex >= 0 && gridIndex < gridData.length) {
          const displayValue = rawToDisplayValue(gridData.valueAt(gridIndex), overlayType);

          return displayValue == null ? null : (
            <MapPositionedText
              key={place.icao}
              testID={place.icao}
              textSizeCache={textSizeCache}
              variant={'regularText30'}
              x={viewPosition[0] / Theme.pixelRatio()}
              y={viewPosition[1] / Theme.pixelRatio()}
            >
              {displayValue}
            </MapPositionedText>
          );
        } else {
          return null;
        }
      });
};

// Stylesheets

const styles = StyleSheet.create({
  layerParent: {
    bottom: Theme.px(0),
    left: Theme.px(0),
    overflow: 'hidden',
    position: 'absolute',
    right: Theme.px(0),
    top: Theme.px(0),
  },
});
